import { Variable } from 'src/api/types';
import { StandardVariableValues } from './types';
import _ from 'lodash';

// Stored platform-variable name per form key. Names are the stable cross-environment
// identifier (platform ids are `500000 + row id` and differ per environment), and the BE
// derives formula tokens from them — matched case-insensitively, like the BE's merge.
const standardVariableNameByKey: Record<keyof StandardVariableValues, string> = {
  annualRevenue: 'Annual revenue',
  numberOfStaff: 'Number of staff',
  annualWageBill: 'Annual wage bill',
  avgWorkingDays: 'Average number of working days per year',
  annualCyberSpend: 'Annual cyber spend',
  avgStaffHardwareCost: 'Average staff hardware cost',
  numberOfCustomers: 'Number of customers',
  avgContractValue: 'Average contract value',
  numberOfDataRecords: 'Number of data records',
  releaseCycleCost: 'Release cycle cost',
  releaseCycleLength: 'Release cycle length',
  researchAndDevelopmentBudget: 'Research and development budget',
  analystLoadedCost: 'Analyst loaded cost',
  managerLoadedCost: 'Manager loaded cost',
  numberOfSuppliers: 'Number of suppliers',
  avgCostToProcureSupplier: 'Average cost to procure supplier',
  avgInvoiceValue: 'Average invoice value',
  largestRegularInvoiceValue: 'Largest regular invoice value',
  cyberInsuranceLimit: 'Cyber insurance limit',
  cyberInsuranceExcess: 'Cyber insurance excess',
  cyberInsurancePremium: 'Cyber insurance premium',
};

export const genStandardVariableValues = (variables: Variable[]): StandardVariableValues => {
  const valueByLowerCaseName: Record<string, Variable['value']> = {};
  variables.forEach((variable) => {
    const key = variable.name.toLowerCase();
    if (!(key in valueByLowerCaseName)) {
      valueByLowerCaseName[key] = variable.value;
    }
  });

  return _.mapValues(standardVariableNameByKey, (name) => valueByLowerCaseName[name.toLowerCase()] || null);
};

export type GetIsStandardVariablesComplete = (
  variables: StandardVariableValues | null,
  params: { developsSoftware: boolean; cyberInsurance: boolean },
) => boolean;

export const getIsStandardVariablesComplete: GetIsStandardVariablesComplete = (
  variables,
  { developsSoftware, cyberInsurance },
) => {
  if (!variables) {
    return false;
  }
  let testVars = _.cloneDeep(variables);
  // CRPD-7261: the three new platform variables never gate completeness — Number of
  // suppliers has no platform default, so counting it would auto-open the wizard for
  // every existing workspace.
  testVars = _.omit(testVars, ['analystLoadedCost', 'managerLoadedCost', 'numberOfSuppliers']);
  if (!developsSoftware) {
    testVars = _.omit(testVars, ['releaseCycleCost', 'releaseCycleLength', 'researchAndDevelopmentBudget']);
  }
  if (!cyberInsurance) {
    testVars = _.omit(testVars, ['cyberInsuranceLimit', 'cyberInsuranceExcess', 'cyberInsurancePremium']);
  }

  return Object.values(testVars).every((v) => !_.isNil(v));
};
